import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export const enum AudioPlayerVariant {
  Stacked = 'stacked',
  Inline = 'inline',
  Analytics = 'analytics',
  Recording = 'recording',
}

const AudioPlayerParts = [
  'outerContainer',
  'errorContainer',
  'errorText',
  'loadingContainer',
  'container',
  'headerContainer',
  'titleText',
  'controlContainer',
  'controlButtonsContainer',
  'playbackContainer',
  'playbackSlider',
  'playbackSliderTrack',
  'playbackSliderFilledTrack',
  'playbackSliderThumbContainer',
  'playbackSliderThumb',
  'playbackSliderThumbInner',
  'playbackSliderVerticalBar',
  'playbackTimestampText',
  'playbackSpeedContainer',
  'playbackSpeedMenu',
  'playbackSpeedMenuItem',
  'playbackSpeedMenuItemText',
  'transcriptionContainer',
  'transcriptionTitleText',
  'transcriptionBodyText',
] as const

const helpers = createMultiStyleConfigHelpers(AudioPlayerParts)
export const AudioPlayerStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    outerContainer: {
      width: 'full',
    },
    errorContainer: {
      width: 'full',
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorText: {
      ...textStyles[textStyleKeys.Body1_Bold],
    },
    loadingContainer: {
      width: 'full',
      alignItems: 'center',
      justifyContent: 'center',
    },
    container: {
      flexDirection: 'column',
      padding: '1.25rem',
      gap: '0.5rem',
      borderRadius: '0.5rem',
      border: '1px solid',
      borderColor: 'border.muted',
      backgroundColor: 'bg.panel',
    },
    headerContainer: {
      alignItems: 'center',
    },
    titleText: {
      ...textStyles[textStyleKeys.Body1_Bold],
    },
    controlContainer: {
      alignItems: 'center',
      gap: '1.75rem',
    },
    controlButtonsContainer: {
      alignItems: 'center',
      gap: '0.5rem',
    },
    playbackContainer: {
      alignItems: 'flex-start',
      flexGrow: 1,
      gap: '0',
      flexDirection: 'column',
    },
    playbackSlider: {
      height: '1.5rem',
    },
    playbackSliderTrack: {
      height: '0.5rem',
      borderRadius: '1rem',
      backgroundColor: 'bg.emphasized',
      cursor: 'pointer',
    },
    playbackSliderFilledTrack: {
      backgroundColor: 'brand.solid',
      cursor: 'pointer',
    },
    playbackSliderThumbContainer: {
      boxSizing: 'content-box',
      boxSize: '1rem',
      backgroundColor: 'transparent',
      padding: '2px',
      cursor: 'pointer',
      _hover: {
        backgroundColor: 'fg.default',
        transform: 'scale(1.1)',
      },
      _active: {
        backgroundColor: 'fg.emphasized',
        transform: 'scale(1.05)',
      },
    },
    playbackSliderThumb: {
      padding: '2px',
      backgroundColor: 'fg.default',
      borderRadius: '1.25rem',
    },
    playbackSliderThumbInner: {
      width: '1rem',
      height: '1rem',
      borderRadius: '50%',
      backgroundColor: 'brand.solid',
    },
    playbackTimestampText: {
      minWidth: '2.125rem',
      ...textStyles[textStyleKeys.Body3],
      color: 'fg.muted',
    },
    playbackSpeedContainer: {},
    playbackSpeedMenu: {
      top: '2rem',
      right: 'px',
      zIndex: 2,
      flexDirection: 'column',
      justifyContent: 'center',
      width: '6.25rem',
      padding: '0.25rem',
      borderRadius: '0.5rem',
      border: '1px solid',
      borderColor: 'border.default',
      backgroundColor: 'bg.panel',
      boxShadow: asChakraVariable({ default: 'shadows.default' }),
    },
    playbackSpeedMenuItem: {
      alignItems: 'center',
      paddingY: '0.5rem',
      paddingX: '0.75rem',
      borderRadius: '0.5rem',
      _hover: {
        backgroundColor: 'bg.muted',
      },
      _active: {
        backgroundColor: 'bg.emphasized',
      },
    },
    playbackSpeedMenuItemText: {
      ...textStyles[textStyleKeys.Body1],
      userSelect: 'none',
    },
    transcriptionContainer: {
      flexDirection: 'column',
      padding: '1.25rem',
      gap: '0.5rem',
      borderRadius: '0.5rem',
      border: '1px solid',
      borderColor: 'border.default',
      backgroundColor: 'bg.subtle',
    },
    transcriptionTitleText: {
      ...textStyles[textStyleKeys.Body1_Bold],
    },
    transcriptionBodyText: {
      ...textStyles[textStyleKeys.Body1],
    },
    playbackSliderVerticalBar: {
      display: 'none',
    },
  },
  variants: {
    [AudioPlayerVariant.Inline]: {
      container: {
        width: 'full',
      },
    },
    [AudioPlayerVariant.Stacked]: {
      container: {
        width: 'full',
      },
      controlContainer: {
        justifyContent: 'space-between',
        width: 'full',
      },
      controlButtonsContainer: {
        flexGrow: 1,
        justifyContent: 'center',
      },
    },
    [AudioPlayerVariant.Recording]: {
      container: {
        width: 'full',
        border: 'none',
        backgroundColor: 'transparent',
      },
      controlContainer: {
        justifyContent: 'space-between',
        width: 'full',
      },
      controlButtonsContainer: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingLeft: '4.25rem',
      },
    },
    [AudioPlayerVariant.Analytics]: {
      container: {
        width: 'full',
        border: 'none',
        backgroundColor: 'transparent',
        padding: '0 0 1.25rem 0',
      },
      controlContainer: {
        justifyContent: 'space-between',
        width: 'full',
      },
      controlButtonsContainer: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingLeft: '4.25rem',
      },
      playbackSliderVerticalBar: {
        display: 'block',
        position: 'absolute',
        top: '1.25rem',
        height: '3.375rem',
        width: '0.125rem',
        backgroundColor: 'fg.default',
        borderRight: '1px solid',
        borderRightColor: 'bg.muted',
        _groupHover: {
          top: '1.325rem',
          height: '3.3125rem',
        },
      },
    },
  },
})

export const useAudioPlayerStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(AudioPlayerStyle, props) as MultiStyleType<
    typeof AudioPlayerParts
  >
}
